import React, { useState } from 'react';
import { Calculator } from '../../types';
import { CalculatorShell } from './CalculatorShell';
import { AllCalculatorsModule } from './AllCalculatorsModule';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';

// Import individual calculators
import IRPFCalculator from './finance/IRPFCalculator';
import InvestmentCalculator from './finance/InvestmentCalculator';
import PercentageCalculator from './finance/PercentageCalculator';
import InterestCalculator from './finance/InterestCalculator';
import FuelCalculator from './finance/FuelCalculator';
import RuleOfThreeCalculator from './finance/RuleOfThreeCalculator';
import EquivalentRateCalculator from './finance/EquivalentRateCalculator';
import FinancingCalculator from './finance/FinancingCalculator';
import FuelConsumptionCalculator from './finance/FuelConsumptionCalculator';
import CurrencyConverter from './finance/CurrencyConverter';
import CashbackCalculator from './finance/CashbackCalculator';
import PointsMilesCalculator from './finance/PointsMilesCalculator';


interface FinanceModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const FinanceModule: React.FC<FinanceModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const supportedSlugs = [
    'imposto-de-renda',
    'investimentos',
    'porcentagem',
    'porcentagem-completa',
    'juros-compostos',
    'juros-simples',
    'financiamento-sac',
    'financiamento-price',
    'taxa-equivalente',
    'consumo-combustivel',
    'moedas',
    'cashback',
    'pontos-milhas',
    'alcool-ou-gasolina',
    'regra-de-tres-simples'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderCalculator(calculator.slug, (data) => setExportData({ title: calculator.title, results: data }))}
    </CalculatorShell>
  );
};

function renderCalculator(slug: string, onResultChange: (data: { label: string, value: string }[]) => void) {
  switch (slug) {
    case 'imposto-de-renda': return <IRPFCalculator onResultChange={onResultChange} />;
    case 'investimentos': return <InvestmentCalculator onResultChange={onResultChange} />;
    case 'porcentagem': return <PercentageCalculator onResultChange={onResultChange} />;
    case 'porcentagem-completa': return <PercentageCalculator onResultChange={onResultChange} />;
    case 'juros-compostos':
    case 'juros-simples': return <InterestCalculator type={slug.includes('simples') ? 'simple' : 'compound'} onResultChange={onResultChange} />;
    case 'financiamento-sac': return <FinancingCalculator type="sac" onResultChange={onResultChange} />;
    case 'financiamento-price': return <FinancingCalculator type="price" onResultChange={onResultChange} />;
    case 'taxa-equivalente': return <EquivalentRateCalculator onResultChange={onResultChange} />;
    case 'consumo-combustivel': return <FuelConsumptionCalculator onResultChange={onResultChange} />;
    case 'moedas': return <CurrencyConverter onResultChange={onResultChange} />;
    case 'cashback': return <CashbackCalculator onResultChange={onResultChange} />;
    case 'pontos-milhas': return <PointsMilesCalculator onResultChange={onResultChange} />;
    case 'alcool-ou-gasolina': return <FuelCalculator onResultChange={onResultChange} />;
    case 'regra-de-tres-simples': return <RuleOfThreeCalculator onResultChange={onResultChange} />;
    default: return null;
  }
}
